import { Button, Dialog, Input, NumericInput, VSpacer } from '@/components/DesignSystem';
import { RewardsConstants, ToastMessages } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { PointsHistoryApi } from '@/utilities/api/PointsHistoryApi';
import { getOrderLabel, getOrderProductLabel } from '@/utilities/OrderLabels';
import { OrderTransactionEndpoint } from '@api/endpoints/OrderTransactionEndpoint';
import { ApiOrderTransaction } from '@api/interfaces/ApiOrderTransaction';
import { ApiOrderTransactionProduct } from '@api/interfaces/ApiOrderTransactionProduct';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import HighlightOff from '@mui/icons-material/HighlightOff';
import { Autocomplete, CircularProgress, Stack } from '@mui/material';
import { SharedConfig } from '@shared/SharedConfig';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface EditManualPointsDialogProps {
  farmerId: string,
  onClose: () => void,
  onSaved: () => void,
  pointsHistory: ApiPointsHistory,
}

export const EditManualPointsDialog = ({
  farmerId,
  onClose,
  onSaved,
  pointsHistory,
}: EditManualPointsDialogProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const [amount, setAmount] = useState<number | undefined>(Math.abs(pointsHistory.amount));
  const [selectedOrderId, setSelectedOrderId] = useState(pointsHistory.orderTransactionId);
  const [chosenOrderProduct, setChosenOrderProduct] = (
    useState<ApiOrderTransactionProduct | null>()
  );

  const ordersQuery: OrderTransactionEndpoint.List.Query = {
    farmerId,
    limit: SharedConfig.maxPageLimit,
  };

  const { data: orderTransactions, isFetching: isFetchingOrders } = useQuery(
    [QueryKeys.GET_ORDERS, ordersQuery],
    () => OrderTransactionsApi.list(ordersQuery),
  );

  const { data: loadedOrder, isFetching: isFetchingOrderProducts } = useQuery(
    [QueryKeys.GET_ORDER, selectedOrderId],
    () => OrderTransactionsApi.getOrder(selectedOrderId!),
    { enabled: !!selectedOrderId },
  );

  const orderOptions = orderTransactions?.data ?? [];
  const listedSelectedOrder = orderOptions.find(({ id }) => id === selectedOrderId);
  const selectedOrder: ApiOrderTransaction | null = listedSelectedOrder ?? loadedOrder ?? null;
  const orderAutocompleteOptions = (loadedOrder && !listedSelectedOrder)
    ? [loadedOrder, ...orderOptions]
    : orderOptions;

  const orderProductOptions = (loadedOrder?.products ?? [])
    .filter(({ product }) => !product.isLoyaltySku);

  const existingOrderProduct = loadedOrder?.products.find(
    ({ id }) => id === pointsHistory.orderTransactionProductId,
  ) ?? null;
  const selectedOrderProduct = chosenOrderProduct === undefined
    ? existingOrderProduct
    : chosenOrderProduct;

  const isLoadingSelectedOrder = !!selectedOrderId && !loadedOrder;
  const isAmountValid = amount !== undefined && amount >= RewardsConstants.addRewardsMinLimit;

  const { isLoading: isSaving, mutateAsync: save } = useMutation(
    async () => PointsHistoryApi.updateManualPoints(farmerId, pointsHistory.id, {
      amount,
      orderId: selectedOrderId,
      orderTransactionProductId: selectedOrderProduct?.id ?? null,
    }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(QueryKeys.GET_DASHBOARD_ORDERS);
        await queryClient.invalidateQueries(QueryKeys.GET_ORDER);
        await queryClient.invalidateQueries(QueryKeys.LIST_SUBWALLET_BALANCES);
        openSnackbar(ToastMessages.updateFarmerRewards);
        onSaved();
      },
      onError: (error: Error) => {
        openSnackbar(error.message || t($ => $.common.error.genericError));
      },
    },
  );

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={isLoadingSelectedOrder || !isAmountValid}
          loading={isSaving}
          onClick={() => save()}
          testID="edit-manual-points-save-button"
          variant="contained"
        >
          {t($ => $.common.actions.save)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button
          onClick={onClose}
          {...props}
          color="inherit"
          testID="edit-manual-points-cancel-button"
        >
          {t($ => $.common.actions.cancel)}
        </Button>
      )}
      dialogWidth="640px"
      maxWidth="sm"
      onClose={onClose}
      open
      showCloseButton={false}
      testID="edit-manual-points-dialog"
      title={t($ => $.customerDashboard.pointsActivity.editManualPoints)}
    >
      <Stack width="100%">
        <NumericInput
          label={t($ => $.common.units.points)}
          maxValue={RewardsConstants.addRewardsMaxLimit}
          minValue={RewardsConstants.addRewardsMinLimit}
          onChangeNumber={setAmount}
          testID="edit-manual-points-amount-input"
          value={amount}
        />
        <VSpacer size="7" />
        <Autocomplete
          clearIcon={<HighlightOff />}
          getOptionLabel={(option) => getOrderLabel(option, t)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={isFetchingOrders || isLoadingSelectedOrder}
          onChange={(_, value) => {
            setSelectedOrderId(value?.id ?? null);
            setChosenOrderProduct(null);
          }}
          options={orderAutocompleteOptions}
          renderInput={(params) => (
            <Input
              label={t($ => $.customers.updatePoints.selectOrderOptional)}
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>
                    {(isFetchingOrders || isLoadingSelectedOrder)
                      ? <CircularProgress color="inherit" size={20} />
                      : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              }}
              testID="edit-manual-points-order-input"
            />
          )}
          value={selectedOrder}
        />
        {!!selectedOrderId && (
          <>
            <VSpacer size="7" />
            <Autocomplete
              clearIcon={<HighlightOff />}
              getOptionLabel={(option) => getOrderProductLabel(option, i18n)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={isFetchingOrderProducts}
              onChange={(_, value) => setChosenOrderProduct(value)}
              options={orderProductOptions}
              renderInput={(params) => (
                <Input
                  label={t($ => $.customers.updatePoints.selectLineItemOptional)}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {isFetchingOrderProducts
                          ? <CircularProgress color="inherit" size={20} />
                          : null}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                  testID="edit-manual-points-line-item-input"
                />
              )}
              value={selectedOrderProduct}
            />
          </>
        )}
      </Stack>
    </Dialog>
  );
};
